import { XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CopyCodingAgentPrompt from "@/components/issues/CopyCodingAgentPrompt";

export type IssueDetail = {
  id: string;
  title: string | null;
  description: string | null;
  suggestion: string | null;
  user_impact?: string | null;
  affected_pages?: Array<{ title: string | null; path: string; url?: string | null }>;
  viewports?: string[];
  screenshot?: { image_url: string | null } | null;
};

type Props = {
  issue: IssueDetail;
  n?: number;
  group: "high" | "medium" | "low";
  originEl: HTMLElement | null;
  onClose: () => void;
  portal?: boolean;
};

const OPEN_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const CLOSE_EASE = "cubic-bezier(0.4, 0, 1, 1)";

export default function IssueDetailModal({ issue, n, group, originEl, onClose, portal = false }: Props) {
  const title = issue.title || "Untitled issue";
  const titleId = `issue-detail-modal-title-${issue.id}`;
  const cardRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLButtonElement>(null);
  const originRef = useRef(originEl);
  const onCloseRef = useRef(onClose);
  const closedRef = useRef(false);
  const previousIdRef = useRef<string | null>(null);
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null);
  originRef.current = originEl;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!portal) {
      return;
    }

    setOverlayRoot(document.getElementById("layout-main-overlay"));
  }, [portal]);

  const dismiss = useCallback(() => {
    if (closedRef.current) {
      return;
    }

    if (reduceMotion()) {
      closedRef.current = true;
      onCloseRef.current();
      return;
    }

    const card = cardRef.current;
    const backdrop = backdropRef.current;
    if (!card) {
      closedRef.current = true;
      onCloseRef.current();
      return;
    }

    closedRef.current = true;
    const { dx, dy, scale } = flyFromOrigin(originRef.current, card);
    const anim = card.animate(
      [
        { opacity: 1, transform: "none" },
        { opacity: 0, transform: `translate(${dx}px, ${dy}px) scale(${scale})` },
      ],
      { duration: 200, easing: CLOSE_EASE, fill: "forwards" },
    );
    backdrop?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, easing: CLOSE_EASE, fill: "forwards" });
    anim.onfinish = () => onCloseRef.current();
  }, []);

  useLayoutEffect(() => {
    closedRef.current = false;
    const card = cardRef.current;
    const backdrop = backdropRef.current;
    if (!card || !backdrop || reduceMotion()) {
      return;
    }

    const switching = previousIdRef.current != null && previousIdRef.current !== issue.id;
    previousIdRef.current = issue.id;
    if (switching) {
      return;
    }

    const { dx, dy, scale } = flyFromOrigin(originRef.current, card);
    card.animate(
      [
        { opacity: 0, transform: `translate(${dx}px, ${dy}px) scale(${scale})` },
        { opacity: 1, transform: "none" },
      ],
      { duration: 280, easing: OPEN_EASE, fill: "forwards" },
    );
    backdrop.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: OPEN_EASE, fill: "forwards" });
  }, [issue.id, overlayRoot]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      dismiss();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dismiss]);

  useEffect(() => {
    if (!portal) {
      return;
    }

    const main = document.querySelector("main[scroll-region]");
    if (!(main instanceof HTMLElement)) {
      return;
    }

    const previousOverflow = main.style.overflowY;
    main.style.overflowY = "hidden";

    return () => {
      main.style.overflowY = previousOverflow;
    };
  }, [portal]);

  if (portal && !overlayRoot) {
    return null;
  }

  const modal = (
    <div className="pointer-events-auto absolute inset-0 z-50 flex items-center justify-center p-3 sm:p-6">
      <button
        ref={backdropRef}
        type="button"
        className="absolute inset-0 cursor-pointer bg-gray-900/55"
        aria-label="Close finding"
        onClick={dismiss}
      />
      <div
        ref={cardRef}
        className={`relative z-[1] max-h-[calc(100%-16px)] w-full max-w-[528px] overflow-auto rounded-b-lg bg-white text-left shadow-xl ${cardBorderClass(group)}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          type="button"
          className="absolute right-2.5 top-2.5 z-[2] flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          aria-label="Close"
          onClick={dismiss}
        >
          <XMarkIcon className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>
        <div className="relative px-5 pb-5 pt-4">
          <div className="flex items-center gap-2 pr-7">
            {n != null ? (
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white ${markClass(group)}`}
              >
                {n}
              </span>
            ) : null}
            <span className={`text-xs font-bold uppercase tracking-wider ${sevTextClass(group)}`}>
              {group} priority
            </span>
          </div>
          <h4 id={titleId} className="mb-2 mt-3 text-base font-semibold text-gray-800">
            {title}
          </h4>
          {issue.description ? <p className="m-0 text-sm leading-relaxed text-gray-700">{issue.description}</p> : null}
          {issue.user_impact ? (
            <div className="mt-4 border-t border-gray-200 pt-3.5">
              <p className="mb-1 font-sans text-sm font-semibold text-primary">Impact</p>
              <p className="m-0 text-sm leading-relaxed text-gray-700">{issue.user_impact}</p>
            </div>
          ) : null}
          {issue.suggestion ? (
            <div className={issue.user_impact ? "mt-3" : "mt-4 border-t border-gray-200 pt-3.5"}>
              <p className="mb-1 font-sans text-sm font-semibold text-primary">Fix</p>
              <p className="m-0 text-sm leading-relaxed text-gray-700">{issue.suggestion}</p>
            </div>
          ) : null}
          <CopyCodingAgentPrompt key={issue.id} issue={issue} variant="detail" />
        </div>
      </div>
    </div>
  );

  if (portal && overlayRoot) {
    return createPortal(modal, overlayRoot);
  }

  return modal;
}

function flyFromOrigin(origin: HTMLElement | null, card: HTMLElement) {
  const frame = card.offsetParent instanceof HTMLElement ? card.offsetParent : card;
  const fromBox = origin?.isConnected ? origin : frame;
  const from = fromBox.getBoundingClientRect();
  const to = card.getBoundingClientRect();
  const dx = from.left + from.width / 2 - (to.left + to.width / 2);
  const dy = from.top + from.height / 2 - (to.top + to.height / 2);
  const scale = Math.min(0.42, Math.max(0.08, from.width / Math.max(to.width, 1)));

  return { dx, dy, scale };
}

function reduceMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function cardBorderClass(group: Props["group"]) {
  if (group === "high") return "border-t-[3px] border-t-error";
  if (group === "medium") return "border-t-[3px] border-t-warning";

  return "border-t-[3px] border-t-primary";
}

function markClass(group: Props["group"]) {
  if (group === "high") return "bg-error";
  if (group === "medium") return "bg-warning";

  return "bg-primary";
}

function sevTextClass(group: Props["group"]) {
  if (group === "high") return "text-error-dark";
  if (group === "medium") return "text-warning-dark";

  return "text-primary";
}
